<template>
  <div v-if="userData">
    <!-- Room list, opened from the chat button in the top bar -->
    <v-navigation-drawer
      v-model="listOpen"
      class="chat-drawer"
      right
      fixed
      temporary
      hide-overlay
      :width="$vuetify.breakpoint.smAndDown ? '100%' : 460"
    >
      <div class="d-flex align-center px-5 pt-5 pb-2">
        <v-btn
          v-if="contactsOpen"
          icon
          class="mr-1"
          title="Буцах"
          @click="contactsOpen = false"
        >
          <v-icon color="grey darken-1">mdi-arrow-left</v-icon>
        </v-btn>
        <span class="chat-title">
          {{ contactsOpen ? "Хэнтэй чатлах вэ" : "Чат" }}
        </span>
        <v-spacer />
        <v-btn
          v-if="!contactsOpen"
          icon
          title="Шинэ чат"
          @click="showContacts"
        >
          <v-icon color="grey darken-1">mdi-square-edit-outline</v-icon>
        </v-btn>
        <v-btn icon title="Хаах" @click="listOpen = false">
          <v-icon color="grey darken-1">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-tabs
        v-if="!contactsOpen && isStaff"
        v-model="tab"
        fixed-tabs
        height="42"
        color="green"
        class="chat-tabs"
        :show-arrows="false"
      >
        <v-tab v-for="item in tabs" :key="item.value" class="px-0">
          {{ item.text }}
          <v-chip
            v-if="unreadIn(item.value)"
            x-small
            color="red"
            dark
            class="ml-1"
          >
            {{ unreadIn(item.value) }}
          </v-chip>
        </v-tab>
      </v-tabs>
      <v-divider />

      <div class="px-4 py-3">
        <v-text-field
          v-model="search"
          dense
          rounded
          filled
          flat
          hide-details
          background-color="grey lighten-4"
          prepend-inner-icon="mdi-magnify"
          placeholder="Хайх..."
        />
      </div>

      <!-- Chats -->
      <template v-if="!contactsOpen">
        <div v-if="loadingRooms" class="py-8 text-center">
          <v-progress-circular indeterminate color="green" size="22" />
        </div>
        <div
          v-else-if="!visibleRooms.length"
          class="py-8 px-5 text-center grey--text text-body-2"
        >
          Чат алга — «Холбоо барих»-аас эхлүүлнэ
        </div>
        <v-list v-else class="py-0">
          <v-list-item
            v-for="room in visibleRooms"
            :key="room.id"
            class="px-5 py-1"
            @click="openRoom(room)"
          >
            <v-avatar :color="roomColor(room)" size="52" class="mr-3">
              <v-img v-if="photoOf(room)" :src="photoOf(room)" />
              <span v-else class="white--text text-subtitle-2">
                {{ initial(title(room)) }}
              </span>
            </v-avatar>
            <v-list-item-content>
              <v-list-item-title class="chat-row-name">
                {{ title(room) }}
              </v-list-item-title>
              <div class="d-flex align-center" style="gap: 4px">
                <v-chip
                  v-for="chip in roomChips(room, me.id, names)"
                  :key="chip"
                  x-small
                  label
                  color="grey lighten-3"
                  class="px-2 chat-chip"
                >
                  {{ chip }}
                </v-chip>
              </div>
              <v-list-item-subtitle v-if="room.lastMessage" class="text-caption">
                {{ previewText(room) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <div class="text-caption grey--text text-right">
              <div>{{ timeLabel(room.lastMessageTime) }}</div>
              <v-chip
                v-if="unread[room.id]"
                x-small
                color="red"
                dark
                class="px-2 mt-1 font-weight-bold"
              >
                {{ unread[room.id] }}
              </v-chip>
            </div>
            <v-btn
              v-if="canDeleteRoom(room, me.id)"
              icon
              x-small
              class="ml-1"
              title="Чатыг устгах"
              @click.stop="removeRoom(room)"
            >
              <v-icon x-small color="grey">mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-list-item>
        </v-list>
      </template>

      <!-- Contacts: pick someone and the chat opens -->
      <template v-else>
        <div class="px-4 pb-2">
          <v-btn-toggle v-model="groupMode" dense mandatory color="green">
            <v-btn :value="false" small>Хувийн чат</v-btn>
            <v-btn :value="true" small>Групп чат</v-btn>
          </v-btn-toggle>
        </div>

        <div class="px-4 pb-2 d-flex" style="gap: 8px">
          <v-select
            v-model="pickKind"
            :items="kindItems"
            dense
            hide-details
            rounded
            filled
            flat
            background-color="grey lighten-4"
            @change="loadPeople"
          />
          <v-select
            v-if="pickKind !== 'teacher'"
            v-model="pickClassId"
            :items="classItems"
            dense
            hide-details
            rounded
            filled
            flat
            background-color="grey lighten-4"
            style="max-width: 110px"
            @change="loadPeople"
          />
        </div>

        <div v-if="groupMode && selected.length" class="px-4 pb-2">
          <v-text-field
            v-model="groupName"
            dense
            rounded
            filled
            flat
            hide-details
            background-color="grey lighten-4"
            placeholder="Группын нэр"
            class="mb-2"
          />
          <v-btn
            color="green"
            dark
            depressed
            block
            small
            :loading="starting"
            @click="startGroup"
          >
            Групп үүсгэх ({{ selected.length }})
          </v-btn>
        </div>

        <div v-if="loadingPeople" class="py-8 text-center">
          <v-progress-circular indeterminate color="green" size="22" />
        </div>
        <v-list v-else class="py-0">
          <div
            v-if="!visiblePeople.length"
            class="py-8 px-5 text-center grey--text text-body-2"
          >
            Хүн олдсонгүй
          </div>
          <v-list-item
            v-for="person in visiblePeople"
            :key="person.id"
            class="px-5 py-1"
            @click="groupMode ? toggle(person) : startWith(person)"
          >
            <v-avatar :color="avatarColor(person.id)" size="52" class="mr-3">
              <v-img v-if="person.photo" :src="person.photo" />
              <span v-else class="white--text text-subtitle-2">
                {{ initial(person.name) }}
              </span>
            </v-avatar>
            <v-list-item-content>
              <v-list-item-title class="chat-row-name">
                {{ person.name }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ person.subtitle }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-icon
              v-if="groupMode"
              :color="isSelected(person) ? 'green' : 'grey lighten-1'"
            >
              {{
                isSelected(person)
                  ? "mdi-check-circle"
                  : "mdi-checkbox-blank-circle-outline"
              }}
            </v-icon>
            <v-icon v-else color="grey lighten-1">mdi-message-outline</v-icon>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Open conversation, docked bottom right -->
    <v-card
      v-if="activeRoom"
      class="chat-window"
      :class="{ 'chat-window--small': minimized }"
      elevation="8"
    >
      <div class="chat-window__bar px-3 py-2 d-flex align-center">
        <v-avatar :color="roomColor(activeRoom)" size="34" class="mr-2">
          <v-img v-if="photoOf(activeRoom)" :src="photoOf(activeRoom)" />
          <span v-else class="white--text text-caption">
            {{ initial(title(activeRoom)) }}
          </span>
        </v-avatar>
        <div class="text-truncate">
          <div class="text-body-2 font-weight-bold text-truncate">
            {{ title(activeRoom) }}
          </div>
          <div class="text-caption grey--text">
            {{
              activeRoom.type === "group"
                ? (activeRoom.participants || []).length + " гишүүн"
                : roomKindLabel(activeRoom)
            }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          v-if="activeRoom.type === 'group'"
          icon
          small
          title="Гишүүд"
          @click="openMembers"
        >
          <v-icon small>mdi-account-group-outline</v-icon>
        </v-btn>
        <v-btn
          v-if="canAddMembers(activeRoom, me.id)"
          icon
          small
          title="Гишүүн нэмэх"
          @click="openAddMembers"
        >
          <v-icon small>mdi-account-plus-outline</v-icon>
        </v-btn>
        <v-btn
          v-if="canDeleteRoom(activeRoom, me.id)"
          icon
          small
          title="Чатыг устгах"
          @click="removeRoom(activeRoom)"
        >
          <v-icon small>mdi-trash-can-outline</v-icon>
        </v-btn>
        <v-btn icon small @click="minimized = !minimized">
          <v-icon small>
            {{ minimized ? "mdi-chevron-up" : "mdi-minus" }}
          </v-icon>
        </v-btn>
        <v-btn icon small @click="closeRoom">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider />

      <template v-if="!minimized">
        <div
          ref="scroller"
          class="chat-window__body pa-3"
          @scroll="onScroll"
          @dragover.prevent
          @drop.prevent="dropImage"
        >
          <div v-if="loadingMessages" class="py-6 text-center">
            <v-progress-circular indeterminate color="green" size="20" />
          </div>
          <div
            v-else-if="!messages.length"
            class="py-6 text-center grey--text text-caption"
          >
            Мессеж алга
          </div>

          <template v-for="(message, index) in messages">
          <div v-if="stampFor(index)" :key="'s' + message.id" class="chat-stamp">
            {{ stampFor(index) }}
          </div>
          <div
            :key="message.id"
            class="d-flex mb-3 msg-row"
            :class="isMine(message) ? 'justify-end' : 'justify-start'"
          >
            <v-avatar
              v-if="!isMine(message)"
              :color="avatarColor(message.senderId)"
              size="26"
              class="mr-2 mt-1"
            >
              <v-img
                v-if="senderPhoto(message)"
                :src="senderPhoto(message)"
              />
              <span v-else class="white--text" style="font-size: 11px">
                {{ initial(message.senderName) }}
              </span>
            </v-avatar>

            <div
              class="d-flex flex-column"
              :class="
                message.type === 'poll' && !message.isDeleted
                  ? ''
                  : isMine(message)
                    ? 'align-end'
                    : 'align-start'
              "
              :style="
                message.type === 'poll' && !message.isDeleted
                  ? 'max-width: 92%; width: 300px'
                  : 'max-width: 66%'
              "
            >
              <div
                v-if="!isMine(message) && activeRoom.type === 'group'"
                class="text-caption grey--text mb-1"
              >
                {{ message.senderName
                }}<span v-if="senderSchool(message)"> · {{ senderSchool(message) }}</span>
              </div>
              <div class="bubble-wrap">
              <div
                class="text-body-2 msg-bubble"
                :class="[
                  message.type === 'poll' && !message.isDeleted
                    ? ''
                    : isMine(message)
                      ? 'bubble-mine px-3 py-2'
                      : 'bubble-other px-3 py-2',
                ]"
                @mousedown="_pressStart(message, $event)"
                @mouseup="_pressEnd"
                @mouseleave="_pressEnd"
                @touchstart="_pressStart(message, $event)"
                @touchmove="_pressEnd"
                @touchend="_pressEnd"
                @touchcancel="_pressEnd"
                @dblclick="_quickLike(message)"
                @contextmenu.prevent="_openReactions(message, $event)"
              >
                <div
                  v-if="!message.isDeleted && replyOf(message)"
                  class="reply-quote"
                >
                  <div class="reply-author">{{ replyOf(message).senderName }}</div>
                  <div class="reply-text">{{ replySnippet(replyOf(message)) }}</div>
                </div>
                <span v-if="message.isDeleted" class="font-italic">
                  Устгагдсан мессеж
                </span>

                <chat-poll
                  v-else-if="message.type === 'poll'"
                  :school="school"
                  :room-id="activeRoom.id"
                  :message="message"
                  :me="me"
                  :mine="isMine(message)"
                />

                <!-- An attachment is a download url; show the thing itself. -->
                <div
                  v-else-if="message.type === 'image'"
                  @click="viewImage(message.content)"
                >
                  <v-img
                    :src="message.content"
                    max-width="200"
                    class="rounded chat-image"
                  />
                </div>
                <a
                  v-else-if="message.type === 'file'"
                  :href="message.content"
                  target="_blank"
                  rel="noopener"
                  class="d-flex align-center"
                  :class="isMine(message) ? 'white--text' : ''"
                >
                  <v-icon
                    small
                    left
                    :color="isMine(message) ? 'white' : 'grey darken-1'"
                  >
                    mdi-paperclip
                  </v-icon>
                  {{ message.fileName || "Файл" }}
                </a>

                <span v-else style="white-space: pre-wrap; overflow-wrap: anywhere">
                  <template v-for="(part, i) in messageParts(message.content, message.mentions, me && me.id)">
                    <span
                      v-if="part.mention"
                      :key="i"
                      class="chat-mention"
                      :class="{ 'chat-mention-me': part.me }"
                      >{{ part.text }}</span
                    >
                    <a
                      v-else-if="part.href"
                      :key="i"
                      :href="part.href"
                      target="_blank"
                      rel="noopener"
                      class="chat-link"
                      >{{ part.text }}</a
                    >
                    <template v-else>{{ part.text }}</template>
                  </template>
                </span>
              </div>
              <div
                v-if="!message.isDeleted"
                class="msg-tools"
                :class="isMine(message) ? 'tools-left' : 'tools-right'"
              >
                <v-btn icon x-small title="Реакц" @click="_openReactions(message, $event)">
                  <v-icon size="16" color="grey darken-1">mdi-emoticon-happy-outline</v-icon>
                </v-btn>
                <v-btn icon x-small title="Хариулах" @click="startReply(message)">
                  <v-icon size="16" color="grey darken-1">mdi-reply</v-icon>
                </v-btn>
                <v-btn v-if="canEdit(message)" icon x-small title="Засах" @click="startEdit(message)">
                  <v-icon size="16" color="grey darken-1">mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn v-if="isMine(message)" icon x-small title="Устгах" @click="removeMessage(message)">
                  <v-icon size="16" color="grey darken-1">mdi-delete-outline</v-icon>
                </v-btn>
              </div>
              </div>
              <div
                v-if="_hasReactions(message)"
                class="d-flex mt-1"
                :class="isMine(message) ? 'justify-end' : ''"
              >
                <div class="reaction-row" @click="_openReactionPeople(message)">
                  <span class="reaction-chip">
                    <span
                      v-for="emoji in _reactionEmojiList(message)"
                      :key="emoji"
                      class="reaction-emoji"
                      >{{ emoji }}</span
                    >
                    <span
                      v-if="_reactionTotal(message) > 1"
                      class="reaction-total"
                      >{{ _reactionTotal(message) }}</span
                    >
                  </span>
                </div>
              </div>
              <div
                class="text-caption grey--text mt-1 d-flex align-center"
                :class="isMine(message) ? 'justify-end' : ''"
              >
                <v-chip
                  v-if="message.action"
                  x-small
                  color="green lighten-4"
                  text-color="green darken-3"
                  class="mr-2"
                >
                  <v-icon x-small left>mdi-check</v-icon>{{ message.action.mn }}
                </v-chip>
                <span v-if="canResolve && !isMine(message)" class="mr-1">
                  <v-btn
                    v-for="item in chatActions"
                    :key="item.id"
                    x-small
                    outlined
                    :color="_actionOn(message, item) ? 'green' : 'grey'"
                    class="text-none mr-1"
                    @click="pickAction(message, item)"
                  >
                    {{ item.mn }}
                  </v-btn>
                </span>
                <span v-if="message.isEdited && !message.isDeleted" class="mr-1">Зассан ·</span>
                {{ timeLabel(message.timestamp) }}
                <v-icon
                  v-if="isMine(message)"
                  x-small
                  class="ml-1 seen-tick"
                  :color="seenBy(message) ? 'green' : 'grey lighten-1'"
                  @click.stop="openSeenBy(message)"
                >
                  mdi-check-all
                </v-icon>
              </div>
            </div>
          </div>
          </template>
        </div>

        <div
          v-if="editTarget || replyTarget"
          class="d-flex align-center mx-3 mt-2 px-3 py-2 edit-banner"
        >
          <v-icon small color="green" class="mr-2">{{
            editTarget ? "mdi-pencil-outline" : "mdi-reply"
          }}</v-icon>
          <div class="flex-grow-1 text-truncate">
            <div class="text-caption green--text font-weight-bold">
              {{ editTarget ? "Мессеж засаж байна" : "Хариулах: " + replyTarget.senderName }}
            </div>
            <div class="text-caption grey--text text-truncate">
              {{ editTarget ? editTarget.content : replySnippet(replyTarget) }}
            </div>
          </div>
          <v-btn icon x-small @click="cancelEdit">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <div v-if="chatLock" class="d-flex align-center px-4 py-3 chat-window__composer chat-window__locked">
          <v-icon small color="grey darken-1" class="mr-2">mdi-lock-outline</v-icon>
          Ажлын цаг дууссан. Чат {{ chatLock }} цагт нээлттэй.
        </div>
        <div v-else class="d-flex align-center px-3 py-2 chat-window__composer">
          <div v-if="!chatLock && mentionAt !== null && (loadingMentions || mentionMatches.length)" class="mention-panel">
            <div v-if="loadingMentions" class="d-flex align-center px-3 py-3 grey--text text--darken-1">
              <v-progress-circular indeterminate size="18" width="2" color="green" class="mr-3" />
              Ачаалж байна…
            </div>
            <div
              v-for="(person, i) in mentionMatches"
              :key="'mn' + person.id"
              class="d-flex align-center px-3 py-2 mention-row"
              :class="{ 'is-active': i === mentionIndex }"
              @mouseenter="mentionIndex = i"
              @mousedown.prevent="pickMention(person)"
            >
              <span class="mention-num">{{ i + 1 }}</span>
              <v-avatar size="30" :color="avatarColor(person.id)" class="mr-3">
                <v-img v-if="person.photo" :src="person.photo" />
                <span v-else class="white--text">{{ initial(person.name) }}</span>
              </v-avatar>
              <span class="text-truncate">{{ person.name }}</span>
            </div>
          </div>
          <v-btn icon small title="Санал асуулга" @click="openPoll">
            <v-icon small color="grey darken-1">mdi-poll</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            title="Зураг илгээх"
            :loading="sendingImage"
            @click="$refs.imageInput.click()"
          >
            <v-icon small color="grey darken-1">mdi-image-outline</v-icon>
          </v-btn>
          <input
            ref="imageInput"
            type="file"
            accept="image/*"
            class="d-none"
            @change="pickImage"
          />
          <v-text-field
            ref="draftInput"
            v-model="draft"
            dense
            rounded
            filled
            flat
            hide-details
            background-color="grey lighten-4"
            placeholder="Aa"
            @input="onDraftInput"
            @keydown="onDraftKeydown"
            @keyup.enter="onDraftEnter"
          />
          <v-btn
            icon
            color="green"
            class="ml-2"
            :loading="sending || editSaving"
            :disabled="!draft.trim()"
            @click="send"
          >
            <v-icon>{{ editTarget ? "mdi-check" : "mdi-send" }}</v-icon>
          </v-btn>
        </div>
      </template>
    </v-card>

    <v-dialog v-model="imageViewer" fullscreen>
      <div class="viewer-backdrop" @click="imageViewer = false">
        <div class="viewer-actions" @click.stop>
          <v-btn icon dark class="viewer-action" :loading="downloading" title="Татах" @click="saveImage(viewerSrc)">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon dark class="viewer-action ml-2" title="Хаах" @click="imageViewer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <img :src="viewerSrc" class="viewer-img" @click.stop />
      </div>
    </v-dialog>


    <!-- Ask a poll -->
    <!-- Урт дарахад гарах реакцын эгнээ -->
    <v-menu
      v-model="reactionOpen"
      :position-x="reactionX"
      :position-y="reactionY"
      absolute
      offset-y
      :close-on-content-click="false"
      z-index="20"
    >
      <div class="reaction-bar">
        <span
          v-for="emoji in reactionEmojis"
          :key="emoji"
          class="reaction-pick"
          :class="{ mine: _myReaction(reactionTarget) === emoji }"
          @click="_pickReaction(emoji)"
        >
          {{ emoji }}
        </span>
      </div>

    </v-menu>

    <!-- Хэн ямар реакц дарсан -->
    <v-dialog v-model="seenOpen" max-width="360">
      <v-card>
        <v-card-title class="text-subtitle-2">
          Харсан {{ seenPeople.length }}/{{ seenPeople.length + unseenPeople.length }}
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh; overflow-y: auto" class="pa-0">
          <v-list class="py-0" dense>
            <v-subheader v-if="seenPeople.length">Харсан</v-subheader>
            <v-list-item v-for="person in seenPeople" :key="'s' + person.id">
              <v-avatar :color="avatarColor(person.id)" size="30" class="mr-3">
                <v-img v-if="person.photo" :src="person.photo" />
                <span v-else class="white--text text-caption">
                  {{ initial(person.name) }}
                </span>
              </v-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ person.name }}
                </v-list-item-title>
              </v-list-item-content>
              <span v-if="person.at" class="text-caption grey--text">
                {{ timeLabel(person.at) }}
              </span>
            </v-list-item>
            <v-subheader v-if="unseenPeople.length">Хараагүй</v-subheader>
            <v-list-item v-for="person in unseenPeople" :key="'u' + person.id">
              <v-avatar color="grey lighten-2" size="30" class="mr-3">
                <span class="white--text text-caption">
                  {{ initial(person.name) }}
                </span>
              </v-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2 grey--text">
                  {{ person.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="seenOpen = false">Хаах</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reactionPeopleOpen" max-width="360">
      <v-card>
        <v-card-title class="text-subtitle-2">
          {{ reactionPeople.length }} реакц
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 60vh; overflow-y: auto" class="pa-0">
          <v-list class="py-0">
            <v-list-item v-for="person in reactionPeople" :key="person.id">
              <v-avatar :color="avatarColor(person.id)" size="34" class="mr-3">
                <v-img v-if="person.photo" :src="person.photo" />
                <span v-else class="white--text text-caption">
                  {{ initial(person.name) }}
                </span>
              </v-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ person.name }}
                </v-list-item-title>
              </v-list-item-content>
              <span style="font-size: 20px">{{ person.emoji }}</span>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reactionPeopleOpen = false">Хаах</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pollOpen" max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1">Санал асуулга</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-textarea
            v-model="pollQuestion"
            rows="3"
            auto-grow
            dense
            outlined
            hide-details
            placeholder="Юу асуух вэ?"
            class="mb-3"
          />
          <div class="text-caption grey--text mb-1">ХАРИУЛТУУД</div>
          <div
            v-for="(option, index) in pollOptions"
            :key="index"
            class="d-flex align-center mb-1"
          >
            <span class="text-caption grey--text mr-2">{{ index + 1 }}</span>
            <v-text-field
              v-model="option.text"
              dense
              hide-details
              outlined
              :placeholder="index + 1 + '-р хариулт'"
            />
            <v-btn
              v-if="pollOptions.length > 2"
              icon
              small
              @click="pollOptions.splice(index, 1)"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-btn
            v-if="pollOptions.length < 10"
            text
            small
            color="blue"
            @click="pollOptions.push({ text: '' })"
          >
            <v-icon small left>mdi-plus</v-icon> Хариулт нэмэх
          </v-btn>

          <v-switch
            v-model="pollAnonymous"
            dense
            hide-details
            label="Нэрээ нууцлах"
            class="mt-3"
          />
          <v-switch
            v-model="pollMultiple"
            dense
            hide-details
            label="Олон хариулт сонгох"
          />

          <div v-if="pollPresets.length" class="mt-4">
            <div class="poll-swatch-label">ӨНГӨ</div>
            <div class="poll-swatches">
              <div
                v-for="(preset, index) in pollPresets"
                :key="index"
                class="poll-swatch"
                :style="{
                  background: pollBackgroundOf(preset),
                  border: paleColor(pollBackgroundOf(preset))
                    ? '1px solid #E5E7EB'
                    : 'none',
                }"
                @click="pollColorsPick = preset"
              >
                <v-icon
                  v-if="samePollColors(preset, pollColorsPick)"
                  size="24"
                  :color="pollTextColorOf(preset)"
                >
                  mdi-check-bold
                </v-icon>
                <span v-else :style="{ color: pollTextColorOf(preset) }">Аа</span>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="pollOpen = false">Болих</v-btn>
          <v-btn
            color="green"
            dark
            depressed
            :loading="pollSending"
            @click="askPoll"
          >
            Илгээх
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Members of a group -->
    <v-dialog v-model="membersOpen" max-width="440">
      <v-card v-if="activeRoom">
        <v-card-title class="text-subtitle-1">
          Гишүүд
          <span class="text-caption grey--text ml-2">
            {{ members.length }}
          </span>
          <v-spacer />
          <v-btn
            v-if="manageAuto && iAmAdmin"
            icon
            small
            title="Хасагдсан гишүүд"
            @click="openExcluded"
          >
            <v-icon small>mdi-account-off-outline</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-2" style="max-height: 420px; overflow-y: auto">
          <div v-if="loadingMembers" class="py-6 text-center">
            <v-progress-circular indeterminate color="green" size="22" />
          </div>
          <v-list v-else dense class="py-0">
            <v-list-item
              v-for="(member, index) in members"
              :key="member.id"
              class="px-0"
            >
              <span class="text-caption grey--text mr-3">{{ index + 1 }}</span>
              <v-avatar :color="avatarColor(member.id)" size="34" class="mr-3">
                <v-img v-if="member.photo" :src="member.photo" />
                <span v-else class="white--text text-caption">
                  {{ initial(member.name) }}
                </span>
              </v-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">
                  {{ member.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ member.label }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-chip v-if="member.isAdmin" x-small color="amber" dark class="mr-1">
                Админ
              </v-chip>
              <v-chip
                v-if="pendingTargets.includes(member.id)"
                x-small
                color="red"
                dark
                class="mr-1"
              >
                Хасах хүсэлттэй
              </v-chip>
              <v-menu v-if="memberAction(member)" offset-y left>
                <template #activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon small>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="startMemberAction(member)">
                    <v-list-item-title
                      :class="memberAction(member) === 'exclude' ? 'red--text' : ''"
                    >
                      {{
                        memberAction(member) === "exclude"
                          ? "Хасах"
                          : "Хасах хүсэлт илгээх"
                      }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="membersOpen = false">Хаах</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reason for removing a member of an automatic room -->
    <v-dialog v-model="reasonOpen" max-width="440">
      <v-card class="pb-2">
        <v-card-title class="text-h6 font-weight-bold">
          {{ reasonMember ? reasonMember.name : "" }}-г
          {{ reasonMode === "exclude" ? "хасах" : "хасах хүсэлт" }}
        </v-card-title>
        <v-card-subtitle class="text-body-2 grey--text">
          {{
            reasonMode === "exclude"
              ? "Доорх шалтгаанаас сонгоно уу."
              : "Доорх алхмуудыг дагаж хүсэлтийг илгээнэ үү."
          }}
        </v-card-subtitle>
        <v-progress-linear v-if="memberBusy" indeterminate color="green" height="3" />
        <div
          v-for="(reason, index) in reasons"
          :key="reason.code"
          class="option-card d-flex align-center mx-4 mb-2 pa-3"
          :style="{ background: reasonColor(reason.code, index) + '12' }"
          @click="!memberBusy && pickReason(reason)"
        >
          <v-avatar :color="reasonColor(reason.code, index)" size="40" class="mr-3">
            <span class="white--text font-weight-bold">{{ index + 1 }}</span>
          </v-avatar>
          <div
            class="icon-box mr-3"
            :style="{ background: reasonColor(reason.code, index) + '29' }"
          >
            <v-icon :color="reasonColor(reason.code, index)" size="22">
              {{ reasonIcon(reason.code, index) }}
            </v-icon>
          </div>
          <div class="flex-grow-1 text-body-1 font-weight-bold">
            {{ reason.label }}
          </div>
          <v-icon :color="reasonColor(reason.code, index)">mdi-chevron-right</v-icon>
        </div>
      </v-card>
    </v-dialog>

    <!-- Where to remove from: only managers and directors get here -->
    <v-dialog v-model="scopeOpen" max-width="440" @click:outside="answerScope(null)">
      <v-card class="pb-2">
        <v-card-title class="text-h6 font-weight-bold">
          {{ reasonMember ? reasonMember.name : "" }}-г хаанаас хасах вэ?
        </v-card-title>
        <v-card-subtitle class="text-body-2 grey--text">
          Хамрах хүрээгээ сонгоно уу.
        </v-card-subtitle>
        <div
          class="option-card d-flex align-center mx-4 mb-2 pa-3"
          style="background: #4f7ff012"
          @click="answerScope('room')"
        >
          <v-avatar color="#4F7FF0" size="40" class="mr-3">
            <span class="white--text font-weight-bold">1</span>
          </v-avatar>
          <div class="icon-box mr-3" style="background: #4f7ff029">
            <v-icon color="#4F7FF0" size="22">mdi-chat-outline</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="text-body-1 font-weight-bold">Зөвхөн энэ чатаас</div>
            <div class="text-caption grey--text">Бусад автомат чатад хэвээр үлдэнэ</div>
          </div>
          <v-icon color="#4F7FF0">mdi-chevron-right</v-icon>
        </div>
        <div
          class="option-card d-flex align-center mx-4 mb-2 pa-3"
          style="background: #f43f5e12"
          @click="answerScope('all')"
        >
          <v-avatar color="#F43F5E" size="40" class="mr-3">
            <span class="white--text font-weight-bold">2</span>
          </v-avatar>
          <div class="icon-box mr-3" style="background: #f43f5e29">
            <v-icon color="#F43F5E" size="22">mdi-forum-outline</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="text-body-1 font-weight-bold">Бүх чатаас</div>
            <div class="text-caption grey--text">
              Сургуулийн бүх автомат группээс хасагдана
            </div>
          </div>
          <v-icon color="#F43F5E">mdi-chevron-right</v-icon>
        </div>
      </v-card>
    </v-dialog>

    <!-- Pending removal requests, shown to the admin on entering the room -->
    <v-dialog v-model="pendingOpen" max-width="460">
      <v-card v-if="activeRoom" class="pb-2">
        <v-progress-linear v-if="pendingBusy" indeterminate color="green" height="3" />
        <v-card-title class="text-h6 font-weight-bold">Гишүүн хасах хүсэлт</v-card-title>
        <v-card-subtitle class="text-body-2 grey--text">
          {{ activeRoom.name }} · {{ pendingRequests.length }} хүсэлт
        </v-card-subtitle>
        <div style="max-height: 420px; overflow-y: auto">
          <div
            v-for="(request, index) in pendingRequests"
            :key="request.id"
            class="option-card mx-4 mb-2 pa-3"
            :style="{ background: reasonColor(request.reasonCode, index) + '12' }"
          >
            <div class="d-flex align-center">
              <v-avatar :color="reasonColor(request.reasonCode, index)" size="36" class="mr-3">
                <span class="white--text font-weight-bold">{{ index + 1 }}</span>
              </v-avatar>
              <div
                class="icon-box mr-3"
                :style="{ background: reasonColor(request.reasonCode, index) + '29' }"
              >
                <v-icon :color="reasonColor(request.reasonCode, index)" size="20">
                  {{ reasonIcon(request.reasonCode, index) }}
                </v-icon>
              </div>
              <div class="flex-grow-1 text-truncate">
                <div class="text-body-1 font-weight-bold text-truncate">
                  {{ request.name }}
                </div>
                <div class="text-caption grey--text">
                  <span :style="{ color: reasonColor(request.reasonCode, index) }">
                    {{ request.reasonLabel }}
                  </span>
                  <span v-if="request.requestedByName"> · {{ request.requestedByName }}</span>
                </div>
              </div>
            </div>
            <div class="d-flex mt-3">
              <v-btn
                outlined
                class="flex-grow-1 mr-2"
                color="grey darken-1"
                :disabled="pendingBusy"
                @click="answerRequest(request, false)"
              >
                <v-icon left small>mdi-close</v-icon>
                Татгалзах
              </v-btn>
              <v-btn
                depressed
                class="flex-grow-1"
                color="#10B981"
                dark
                :disabled="pendingBusy"
                @click="answerRequest(request, true)"
              >
                <v-icon left small>mdi-check</v-icon>
                Зөвшөөрөх
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Excluded members, with restore -->
    <v-dialog v-model="excludedOpen" max-width="460">
      <v-card class="pb-2">
        <v-progress-linear v-if="excludedBusy" indeterminate color="green" height="3" />
        <v-card-title class="text-h6 font-weight-bold">Хасагдсан гишүүд</v-card-title>
        <v-card-subtitle class="text-body-2 grey--text">
          {{
            excludedRows.length
              ? excludedRows.length + " хүн · \"Буцаах\" дарж эргүүлж оруулна"
              : "Хасагдсан гишүүн алга."
          }}
        </v-card-subtitle>
        <div style="max-height: 420px; overflow-y: auto">
          <div
            v-for="(row, index) in excludedRows"
            :key="row.scope + row.id"
            class="option-card d-flex align-center mx-4 mb-2 pa-3"
            :style="{ background: reasonColor(row.code, index) + '12' }"
          >
            <v-avatar :color="reasonColor(row.code, index)" size="36" class="mr-3">
              <span class="white--text font-weight-bold">{{ index + 1 }}</span>
            </v-avatar>
            <div
              class="icon-box mr-3"
              :style="{ background: reasonColor(row.code, index) + '29' }"
            >
              <v-icon :color="reasonColor(row.code, index)" size="20">
                {{ reasonIcon(row.code, index) }}
              </v-icon>
            </div>
            <div class="flex-grow-1 text-truncate">
              <div class="text-body-1 font-weight-bold text-truncate">{{ row.name }}</div>
              <div class="text-caption" :style="{ color: reasonColor(row.code, index) }">
                {{ row.label }} · {{ row.scope === "all" ? "бүх чат" : "энэ чат" }}
              </div>
            </div>
            <span
              v-if="row.scope === 'all' && !canGlobal"
              class="text-caption grey--text text-center"
              style="line-height: 1.2"
            >
              Менежер<br />буцаана
            </span>
            <v-btn
              v-else
              outlined
              small
              :color="reasonColor(row.code, index)"
              :disabled="excludedBusy"
              @click="restoreRow(row)"
            >
              Буцаах
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- New chat -->
    <v-dialog v-model="newChatOpen" max-width="520">
      <v-card>
        <v-card-title class="text-subtitle-1">
          {{ addMode ? "Гишүүн нэмэх" : "Шинэ чат" }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-btn-toggle
            v-model="pickKind"
            dense
            mandatory
            color="green"
            class="mb-3"
          >
            <v-btn value="teacher" small>Багш, ажилчид</v-btn>
            <v-btn value="student" small>Сурагч</v-btn>
            <v-btn value="parent" small>Эцэг эх</v-btn>
          </v-btn-toggle>

          <v-select
            v-if="pickKind !== 'teacher'"
            v-model="pickClassId"
            :items="classItems"
            label="Бүлэг"
            dense
            hide-details
            class="mb-3"
            @change="loadPeople"
          />

          <v-text-field
            v-model="search"
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="Нэрээр хайх"
            class="mb-2"
          />
          <v-text-field
            v-if="!addMode && selected.length > 1"
            v-model="groupName"
            dense
            hide-details
            label="Группын нэр"
            class="mb-2"
          />

          <div v-if="loadingPeople" class="py-6 text-center">
            <v-progress-circular indeterminate color="green" size="22" />
          </div>
          <div v-else style="max-height: 300px; overflow-y: auto">
            <div
              v-if="!visiblePeople.length"
              class="py-6 text-center grey--text text-body-2"
            >
              Хүн олдсонгүй
            </div>
            <v-list dense class="py-0">
              <v-list-item
                v-for="person in visiblePeople"
                :key="person.id"
                @click="toggle(person)"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    {{ person.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ person.subtitle }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon
                    :color="isSelected(person) ? 'green' : 'grey lighten-1'"
                  >
                    {{
                      isSelected(person)
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeNewChat">Болих</v-btn>
          <v-btn
            color="green"
            dark
            depressed
            :loading="starting"
            :disabled="!selected.length"
            @click="startChat"
          >
            {{ addMode ? "Нэмэх" : "Эхлүүлэх" }} ({{ selected.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { sync } from "vuex-pathify";
import {
  chatBus,
  watchMyRooms,
  watchMessages,
  touchMyMember,
  sendMessage,
  sendImage,
  deleteMessage,
  editMessage,
  markRead,
  isUnread,
  roomScope,
  roomChips,
  previewText,
  watchUnreadCount,
  ensureSchoolGroups,
  loadPeopleInfo,
  loadMembers,
  syncRoomParticipants,
  canAddMembers,
  millis,
  addMembersToGroup,
  canDeleteRoom,
  canUseChat,
  avatarColor,
  roomAvatarColor,
  deleteRoom,
  otherPhoto,
  otherParticipant,
  openDirectChat,
  createGroupChat,
  loadStaff,
  loadClasses,
  loadClassMembers,
  staffName,
  otherName,
  timeLabel,
  stampLabel,
  needsStamp,
  linkifyParts,
  messageParts,
  downloadFile,
  sendPoll,
  pollColorPresets,
  pollBackgroundOf,
  pollTextColorOf,
  setReaction,
  reactionCounts,
  excludeReasons,
  canManageAutoMembers,
  canExcludeGlobally,
  excludeMember,
  excludedMembers,
  restoreMember,
  requestMemberRemoval,
  pendingRemovalRequests,
  resolveRemovalRequest,
  reasonColor,
  reasonIcon,
  ensureTeacherClassChats,
  ensureStudentClassChat,
  isNationalId,
  setMessageAction,
} from "./chat.js";
import ChatPoll from "./ChatPoll.vue";

// Үндэсний чатын админы бэлэн хариултууд.
const CHAT_ACTIONS = [
  { id: "ok", en: "OK", mn: "Асуудалгүй" },
  { id: "wait", en: "Wait", mn: "Түр хүлээнэ үү" },
];

export default {
  name: "ChatDock",
  components: { ChatPoll },
  data() {
    return {
      listOpen: false,
      resolving: null,
      tab: 0,
      tabs: [
        { value: "official", text: "Албан өрөө" },
        { value: "private", text: "Хувь өрөө" },
      ],
      contactsOpen: false,
      pollOpen: false,
      pollQuestion: "",
      pollOptions: [{ text: "" }, { text: "" }],
      pollAnonymous: true,
      pollMultiple: false,
      pollPresets: [],
      pollColorsPick: null,
      reactionEmojis: ["👍", "❤️", "😂", "😮", "😢", "🙏"],
      reactionOpen: false,
      reactionTarget: null,
      reactionX: 0,
      reactionY: 0,
      reactionPeopleOpen: false,
      seenOpen: false,
      seenPeople: [],
      unseenPeople: [],
      reactionPeople: [],
      pressTimer: null,
      pollSending: false,
      groupMode: false,
      editTarget: null,
      replyTarget: null,
      stuck: true,
      editSaving: false,
      membersOpen: false,
      members: [],
      loadingMembers: false,
      pendingTargets: [],
      reasonOpen: false,
      reasons: [],
      reasonMember: null,
      reasonMode: "",
      memberBusy: false,
      scopeOpen: false,
      scopeResolve: null,
      pendingOpen: false,
      pendingRequests: [],
      pendingBusy: false,
      excludedOpen: false,
      excludedRows: [],
      excludedBusy: false,
      rooms: [],
      loadingRooms: true,
      search: "",

      activeRoom: null,
      chatLock: "",
      minimized: false,
      messages: [],
      loadingMessages: false,
      draft: "",
      mentioned: [],
      roomPeople: null,
      loadingMentions: false,
      mentionAt: null,
      mentionQuery: "",
      mentionIndex: 0,
      sending: false,
      sendingImage: false,
      imageViewer: false,
      downloading: false,
      viewerSrc: "",

      newChatOpen: false,
      addMode: false,
      pickKind: "teacher",
      pickClassId: null,
      classes: [],
      people: [],
      loadingPeople: false,
      selected: [],
      groupName: "",
      starting: false,

      names: {},
      nationalLoaded: {},
      unread: {},
      unreadStops: {},
      stopRooms: null,
      stopMessages: null,
      // Reload-оор дахин үүсэхэд title нь аль хэдийн "(27) " гэсэн угтвартай
      // байдаг тул түүнийг нь салгаж авахгүй бол угтвар нь давхарлаад явна.
      baseTitle: document.title.replace(/^(?:\(\d+\)\s*)+/, ""),
    };
  },
  computed: {
    mentionMatches() {
      if (this.mentionAt === null || !this.roomPeople) return [];
      const q = this.mentionQuery.toLowerCase();
      return this.roomPeople.filter((p) => String(p.name || "").toLowerCase().includes(q));
    },
    ...sync("*"),
    school() {
      return this.userData ? this.userData.school : null;
    },
    year() {
      return this.$store.state.kSchoolStartYear;
    },
    me() {
      return {
        id: this.userData ? String(this.userData.id) : "",
        name: this.userData ? staffName(this.userData) : "",
      };
    },
    classItems() {
      return this.classes.map((c) => ({ text: c.name, value: c.id }));
    },
    /** Сургуулийн автомат групп — гишүүн хасах дүрэм зөвхөн энд. */
    manageAuto() {
      return !!this.activeRoom && canManageAutoMembers(this.activeRoom);
    },
    iAmAdmin() {
      const room = this.activeRoom;
      if (!room) return false;
      return (room.admins || []).includes(this.me.id) || room.createdBy === this.me.id;
    },
    canGlobal() {
      return canExcludeGlobally(this.userData);
    },
    activeTab() {
      const item = this.tabs[this.tab];
      return item ? item.value : "official";
    },
    kindItems() {
      return [
        { value: "teacher", text: "Багш, ажилчид" },
        { value: "student", text: "Сурагч" },
        { value: "parent", text: "Эцэг эх" },
      ];
    },
    /** Only staff see the three tabs; everybody else has one plain list. */
    isStaff() {
      const role = String(
        (this.userData && this.userData.role) || "",
      ).toLowerCase();
      return role !== "student" && role !== "parent";
    },
    /** Direct rooms whose other side was confirmed deleted are hidden
     * everywhere — list, tab badges and the top-bar number alike. */
    liveRooms() {
      return this.rooms.filter((room) => {
        if (room.type === "group") return true;
        const known = this.names[otherParticipant(room, this.me.id)];
        return !(known && known.missing);
      });
    },
    visibleRooms() {
      const query = String(this.search || "").toLowerCase().trim();
      return this.liveRooms.filter((room) => {
        if (this.isStaff && roomScope(room) !== this.activeTab) return false;
        if (!query) return true;
        return this.title(room).toLowerCase().includes(query);
      });
    },
    visiblePeople() {
      // The one search box on top filters whatever the list is showing.
      const query = String(this.search || "").toLowerCase().trim();
      if (!query) return this.people;
      return this.people.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          String(p.subtitle || "").toLowerCase().includes(query),
      );
    },
    unreadRooms() {
      return this.liveRooms.filter((room) => this.unread[room.id] > 0).length;
    },
    /** «Асуудалгүй» товч зөвхөн үндэсний өрөөнд, superadmin-д. Өрөөний
     * `admins` жагсаалтаар биш үүргээр нь шалгана — тэр жагсаалтад
     * шинэ админ cloud function ажиллах хүртэл ордоггүй. */
    chatActions() {
      return CHAT_ACTIONS;
    },
    canResolve() {
      const room = this.activeRoom;
      if (!room || !isNationalId(room.id)) return false;
      const role = this.userData && this.userData.role;
      return role === "superadmin" || role === "superadmin2";
    },

    /** Messages, not rooms — the number shown on the top-bar button. */
    totalUnread() {
      return this.liveRooms.reduce(
        (sum, room) => sum + (this.unread[room.id] || 0),
        0,
      );
    },
  },
  created() {
    if (!canUseChat(this.userData) || !this.school.ref) return;

    chatBus.$on("toggle", this.toggleList);
    ensureSchoolGroups(this.school, this.me.id).catch((e) =>
      console.log("chat groups sync failed", e),
    );
    // Ангийн чат — аппын багш, сурагчийн чатын дэлгэц нээгдэхэд хийдэгтэй ижил.
    const role = String((this.userData && this.userData.role) || "").toLowerCase();
    const classChats =
      role === "student"
        ? ensureStudentClassChat(this.school, this.userData)
        : role === "parent"
          ? Promise.resolve()
          : ensureTeacherClassChats(this.school, this.userData);
    classChats.catch((e) => console.log("class chats sync failed", e));

    this.stopRooms = watchMyRooms(this.school, this.me.id, (rooms) => {
      this.rooms = rooms;
      this.loadingRooms = false;
      this.resolveNames(rooms);
      this.watchUnread(rooms);
      chatBus.$emit("unread", this.totalUnread);
      this.showUnreadInTitle();

      if (this.activeRoom) {
        const fresh = rooms.find((r) => r.id === this.activeRoom.id);
        if (fresh) this.activeRoom = fresh;
      }
    });
  },
  beforeDestroy() {
    this._pressEnd();
    chatBus.$off("toggle", this.toggleList);
    if (this.stopRooms) this.stopRooms();
    if (this.stopMessages) this.stopMessages();
    for (const stop of Object.values(this.unreadStops)) stop();
    document.title = this.baseTitle;
  },
  methods: {
    // National rooms may set working hours (Ulaanbaatar time); outside them only superadmins write.
    lockFor(room) {
      const gs = (room && room.groupSettings) || {};
      if (!room || !isNationalId(room.id) || !gs.openTime || !gs.closeTime) return "";
      const role = this.userData && this.userData.role;
      if (role === "superadmin" || role === "superadmin2") return "";
      const minutes = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
      const ub = new Date(Date.now() + 8 * 3600 * 1000);
      const now = ub.getUTCHours() * 60 + ub.getUTCMinutes();
      const open = minutes(gs.openTime);
      const close = minutes(gs.closeTime);
      const inside = open <= close ? now >= open && now < close : now >= open || now < close;
      return inside ? "" : `${gs.openTime}–${gs.closeTime}`;
    },
    reasonColor,
    reasonIcon,
    isUnread,
    timeLabel,
    linkifyParts,
    messageParts,
    pollBackgroundOf,
    pollTextColorOf,
    reactionCounts,
    _hasReactions(message) {
      return Object.keys((message || {}).reactions || {}).length > 0;
    },
    // Хамгийн олон дарагдсан нь эхэнд — аппынхтай ижил эрэмбэ.
    _reactionEmojiList(message) {
      const counts = reactionCounts(message);
      return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
    },
    _reactionTotal(message) {
      return Object.keys((message || {}).reactions || {}).length;
    },
    _myReaction(message) {
      return ((message || {}).reactions || {})[this.me.id] || "";
    },
    // Хулгана эсвэл хуруу 450ms дарж байвал реакцын эгнээ гарна — баруун
    // товч бас ажиллана.
    // window.event нь Chrome-оос өөр хөтөч дээр хоосон ирдэг тул үйл явдлыг
    // template-ээс нь шууд дамжуулна. Гүйлгэхэд цуцлахгүй бол чатаа
    // гүйлгэхэд л реакцын эгнээ дэлгэгдэнэ.
    _pressStart(message, event) {
      this._pressEnd();
      const point = event && event.touches ? event.touches[0] : event;
      const x = point ? point.clientX : 0;
      const y = point ? point.clientY : 0;
      this.pressTimer = setTimeout(() => {
        this._openReactions(message, x, y);
      }, 450);
    },
    _pressEnd() {
      if (this.pressTimer) {
        clearTimeout(this.pressTimer);
        this.pressTimer = null;
      }
    },
    _openReactions(message, x, y) {
      if (message.isDeleted) return;
      const point = x && x.clientX !== undefined ? x : null;
      this.reactionTarget = message;
      this.reactionX = point ? point.clientX : x || 0;
      this.reactionY = point ? point.clientY : y || 0;
      this.reactionOpen = true;
    },
    // Facebook-ийн адил хоёр товшилт нь шууд 👍 — дахин хоёр товшивол буцаана.
    async _quickLike(message) {
      this._pressEnd();
      if (message.isDeleted) return;
      const next = this._myReaction(message) === "👍" ? null : "👍";
      try {
        await setReaction(
          this.school,
          this.activeRoom.id,
          message.id,
          this.me.id,
          next
        );
      } catch (e) {
        console.log("реакц хадгалж чадсангүй", e);
      }
    },
    async _pickReaction(emoji) {
      const message = this.reactionTarget;
      this.reactionOpen = false;
      if (!message) return;
      const next = this._myReaction(message) === emoji ? null : emoji;
      try {
        await setReaction(
          this.school,
          this.activeRoom.id,
          message.id,
          this.me.id,
          next
        );
      } catch (e) {
        console.log("реакц хадгалж чадсангүй", e);
      }
    },
    async _openReactionPeople(message) {
      const map = (message || {}).reactions || {};
      const ids = Object.keys(map);
      if (!ids.length) return;

      const info = await loadPeopleInfo(this.school, this.year, ids);
      this.reactionPeople = ids
        .map((id) => ({
          id: id,
          name: (info[id] || {}).name || "Хэрэглэгч",
          photo: (info[id] || {}).photo || "",
          emoji: map[id],
        }))
        .sort((a, b) => a.name.localeCompare(b.name, "mn"));
      this.reactionPeopleOpen = true;
    },
    samePollColors(a, b) {
      if (!a || !b) return false;
      return (a.bg || "") === (b.bg || "") && (a.text || "") === (b.text || "");
    },
    paleColor(hex) {
      const digits = String(hex).replace("#", "");
      if (digits.length !== 6) return true;
      const r = parseInt(digits.slice(0, 2), 16) / 255;
      const g = parseInt(digits.slice(2, 4), 16) / 255;
      const b = parseInt(digits.slice(4, 6), 16) / 255;
      return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.85;
    },
    roomChips,
    previewText,
    /// Аппын мессежийн жагсаалттай ижил: өдөр солигдох эсвэл 60 минут
    /// зөрөх бүрд цагийн тэмдэг гарна.
    stampFor(index) {
      const message = this.messages[index];
      const older = index > 0 ? this.messages[index - 1] : null;
      return needsStamp(message, older) ? stampLabel(message.timestamp) : "";
    },
    unreadIn(tabValue) {
      return this.liveRooms
        .filter((room) => roomScope(room) === tabValue)
        .reduce((sum, room) => sum + (this.unread[room.id] || 0), 0);
    },
    /** Name and role/class for the other side of every direct room. */
    async resolveNames(rooms) {
      const missing = rooms
        .filter((room) => room.type !== "group")
        .map((room) => otherParticipant(room, this.me.id))
        .filter((id) => id && !this.names[id]);

      if (!missing.length) return;
      const found = await loadPeopleInfo(this.school, this.year, missing);
      // Ids the search could not find anywhere are marked, so the row can
      // say the account was deleted instead of a bare "Чат".
      const gone = {};
      for (const id of missing) if (!found[id]) gone[id] = { missing: true };
      this.names = { ...this.names, ...found, ...gone };
    },
    /** One counter per room, exactly like the phone's list. */
    watchUnread(rooms) {
      // Only a room with something new keeps a message listener; a fully read
      // one counts 0 straight from its document, the phone's rule. A new
      // message updates the room and brings the listener back. A deleted room
      // drops out of `rooms` and loses its listener the same way.
      const want = new Set(
        rooms
          .filter((room) => !room.lastMessageId || isUnread(room, this.me.id))
          .map((room) => room.id),
      );
      for (const id of Object.keys(this.unreadStops)) {
        if (want.has(id)) continue;
        this.unreadStops[id]();
        delete this.unreadStops[id];
        this.$delete(this.unread, id);
      }

      for (const room of rooms) {
        if (!want.has(room.id) || this.unreadStops[room.id]) continue;
        this.unreadStops[room.id] = watchUnreadCount(
          this.school,
          room.id,
          this.me.id,
          (count) => {
            this.$set(this.unread, room.id, count);
            chatBus.$emit("unread", this.totalUnread);
            this.showUnreadInTitle();
          },
        );
      }
    },
    toggleList() {
      this.listOpen = !this.listOpen;
      if (!this.listOpen) this.contactsOpen = false;
    },
    async openMembers() {
      this.membersOpen = true;
      this.loadingMembers = true;
      try {
        this.members = await loadMembers(this.school, this.year, this.activeRoom);
        if (this.manageAuto) {
          const pending = await pendingRemovalRequests(this.school, this.activeRoom.id);
          this.pendingTargets = pending.map((r) => String(r.targetId || r.id));
          if (this.iAmAdmin && pending.length) await this.checkPending(this.activeRoom);
        }
      } finally {
        this.loadingMembers = false;
      }
    },
    /** 'exclude' — админ шууд хасна; 'request' — гишүүн хүсэлт явуулна;
     *  null — үйлдэлгүй. Захирлыг хэн ч хасахгүй; менежерийг зөвхөн
     *  менежер, захирал хасна. */
    memberAction(member) {
      if (!this.manageAuto) return null;
      if (member.id === this.me.id) return null;
      if (member.id === this.activeRoom.createdBy) return null;
      const role = String(member.role || "").toLowerCase();
      if (role === "schooldirector") return null;
      if (role === "schoolmanager" && !this.canGlobal) return null;
      if (this.iAmAdmin) return "exclude";
      if (this.pendingTargets.includes(member.id)) return null;
      return "request";
    },
    async startMemberAction(member) {
      this.reasonMember = member;
      this.reasonMode = this.memberAction(member);
      if (!this.reasons.length) this.reasons = await excludeReasons();
      this.reasonOpen = true;
    },
    askScope() {
      this.scopeOpen = true;
      return new Promise((resolve) => (this.scopeResolve = resolve));
    },
    answerScope(scope) {
      this.scopeOpen = false;
      if (this.scopeResolve) this.scopeResolve(scope);
      this.scopeResolve = null;
    },
    async confirmExclude(name, scope) {
      const where = scope === "all" ? "сургуулийн бүх автомат чатаас" : "энэ чатаас";
      const result = await this.$swal.fire({
        title: "Хасах уу?",
        text: `${name}-г ${where} хасах гэж байна. Итгэлтэй байна уу?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Тийм, хас",
        cancelButtonText: "Болих",
      });
      return result.isConfirmed;
    },
    async pickReason(reason) {
      const member = this.reasonMember;
      if (!member) return;

      if (this.reasonMode === "request") {
        this.memberBusy = true;
        const error = await requestMemberRemoval(
          this.school,
          this.activeRoom,
          this.userData,
          member.id,
          reason,
        );
        this.memberBusy = false;
        this.reasonOpen = false;
        if (error) return this.$swal.fire("", error, "warning");
        this.pendingTargets.push(member.id);
        return this.$swal.fire("", "Хүсэлт админд илгээгдлээ", "success");
      }

      // Менежер, захирал хүрээгээ сонгоно; багш админ зөвхөн энэ чатаас.
      let scope = "room";
      if (this.canGlobal) {
        scope = await this.askScope();
        if (!scope) return;
      }
      if (!(await this.confirmExclude(member.name, scope))) return;

      this.memberBusy = true;
      const error = await excludeMember(
        this.school,
        this.activeRoom,
        this.userData,
        member.id,
        reason,
        scope,
      );
      this.memberBusy = false;
      this.reasonOpen = false;
      if (error) return this.$swal.fire("", error, "warning");
      this.members = this.members.filter((m) => m.id !== member.id);
      this.pendingTargets = this.pendingTargets.filter((id) => id !== member.id);
      this.$swal.fire("", "Хасагдлаа", "success");
    },
    /** Автомат группын админ өрөөнд ормогц хүлээгдэж буй хүсэлтээ харна. */
    async checkPending(room) {
      if (!canManageAutoMembers(room)) return;
      const admins = room.admins || [];
      if (!admins.includes(this.me.id)) return;
      const requests = await pendingRemovalRequests(this.school, room.id);
      if (!requests.length) return;
      if (!this.activeRoom || this.activeRoom.id !== room.id) return;
      const info = await loadPeopleInfo(
        this.school,
        this.year,
        requests.map((r) => String(r.targetId || r.id)),
      );
      this.pendingRequests = requests.map((r) => ({
        ...r,
        name: (info[String(r.targetId || r.id)] || {}).name || "Тодорхойгүй",
      }));
      this.pendingOpen = true;
    },
    async answerRequest(request, approve) {
      let scope = "room";
      if (approve) {
        this.reasonMember = request;
        if (this.canGlobal) {
          scope = await this.askScope();
          if (!scope) return;
        }
        if (!(await this.confirmExclude(request.name, scope))) return;
      } else {
        const result = await this.$swal.fire({
          title: "Татгалзах уу?",
          text: `${request.name}-г хасах хүсэлтийг татгалзах гэж байна.`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Татгалзах",
          cancelButtonText: "Болих",
        });
        if (!result.isConfirmed) return;
      }

      this.pendingBusy = true;
      const error = await resolveRemovalRequest(
        this.school,
        this.activeRoom,
        this.userData,
        request,
        approve,
        scope,
      );
      this.pendingBusy = false;
      if (error) return this.$swal.fire("", error, "warning");
      this.pendingRequests = this.pendingRequests.filter((r) => r.id !== request.id);
      this.pendingTargets = this.pendingTargets.filter((id) => id !== request.id);
      if (approve) this.members = this.members.filter((m) => m.id !== request.id);
      if (!this.pendingRequests.length) this.pendingOpen = false;
      this.$swal.fire(
        "",
        approve
          ? `${request.name} хасагдлаа`
          : `${request.name}-г хасах хүсэлтийг татгалзлаа`,
        "success",
      );
    },
    async openExcluded() {
      this.excludedOpen = true;
      this.excludedBusy = true;
      try {
        this.excludedRows = await excludedMembers(this.school, this.year, this.activeRoom);
      } finally {
        this.excludedBusy = false;
      }
    },
    async restoreRow(row) {
      if (row.scope === "all") {
        const result = await this.$swal.fire({
          title: "Буцаах уу?",
          text: `${row.name} сургуулийн бүх автомат чатад сэргээгдэнэ. Итгэлтэй байна уу?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Тийм, буцаа",
          cancelButtonText: "Болих",
        });
        if (!result.isConfirmed) return;
      }
      this.excludedBusy = true;
      const error = await restoreMember(this.school, this.activeRoom, this.userData, row);
      this.excludedBusy = false;
      if (error) return this.$swal.fire("", error, "warning");
      this.excludedRows = this.excludedRows.filter(
        (r) => !(r.id === row.id && r.scope === row.scope),
      );
      this.members = await loadMembers(this.school, this.year, this.activeRoom);
      this.$swal.fire("", "Буцаагдлаа", "success");
    },
    async showContacts() {
      this.contactsOpen = true;
      this.search = "";
      if (!this.classes.length) {
        this.classes = await loadClasses(this.school, this.year);
        if (this.classes.length) this.pickClassId = this.classes[0].id;
      }
      await this.loadPeople();
    },
    async startGroup() {
      if (!this.selected.length) return;
      if (!this.groupName.trim()) {
        this.$swal.fire("", "Группын нэрээ бичнэ үү", "warning");
        return;
      }

      this.starting = true;
      try {
        const room = await createGroupChat(
          this.school,
          this.me,
          this.selected,
          this.groupName.trim(),
        );
        this.selected = [];
        this.groupName = "";
        this.groupMode = false;
        this.contactsOpen = false;
        this.openRoom(room);
      } catch (e) {
        this.$swal.fire("Алдаа", "Групп үүсгэж чадсангүй", "error");
      } finally {
        this.starting = false;
      }
    },
    async startWith(person) {
      const room = await openDirectChat(this.school, this.me, person);
      this.contactsOpen = false;
      this.openRoom(room);
    },
    title(room) {
      return otherName(room, this.me.id, this.names);
    },
    initial(text) {
      return String(text || "?").trim().charAt(0).toUpperCase();
    },
    photoOf(room) {
      // Группт админ зураг тавьсан бол гишүүдийн зургийн оронд түүнийг.
      if (room.type === "group" && room.photoURL) return room.photoURL;
      return otherPhoto(room, this.me.id, this.names);
    },
    /** Sender's school for national rooms, from the members subcollection. */
    async loadNationalNames(room) {
      if (this.nationalLoaded[room.id]) return;
      const people = await loadMembers(this.school, this.year, room);
      const found = {};
      for (const p of people) {
        found[p.id] = { name: p.name, photo: p.photo, school: p.school || "" };
      }
      this.names = { ...this.names, ...found };
      this.$set(this.nationalLoaded, room.id, true);
    },
    senderSchool(message) {
      const known = this.names[message.senderId];
      return (known && known.school) || "";
    },
    senderPhoto(message) {
      const known = this.names[message.senderId];
      return (known && known.photo) || "";
    },
    avatarColor: avatarColor,
    /** A room with no picture wears its own colour, like the phone. */
    roomColor(room) {
      return roomAvatarColor(room, this.me.id);
    },
    isMine(message) {
      return message.senderId === this.me.id;
    },
    _actionOn(message, item) {
      return !!message.action && message.action.id === item.id;
    },
    /** Дахин дарвал тэмдэг арилна, өөрийг нь дарвал солигдоно. */
    async pickAction(message, item) {
      this.resolving = message.id;
      try {
        await setMessageAction(
          this.school,
          this.activeRoom.id,
          message.id,
          this.me,
          this._actionOn(message, item) ? null : item,
        );
      } catch (error) {
        this.$swal.fire({
          icon: "error",
          title: "Тэмдэглэж чадсангүй",
          text: error.message || String(error),
        });
      } finally {
        this.resolving = null;
      }
    },
    /** Somebody other than the sender has opened it. */
    /// Аппын «Харсан / Хараагүй» жагсаалттай ижил.
    async openSeenBy(message) {
      const room = this.activeRoom;
      if (!room || room.type !== 'group') return;
      const people = await loadMembers(this.school, this.year, room);
      const readBy = message.readBy || {};
      const readAt = message.readAt || {};
      const others = people.filter((p) => p.id !== message.senderId);
      this.seenPeople = others
        .filter((p) => readBy[p.id] === true)
        .map((p) => ({ ...p, at: readAt[p.id] || null }))
        .sort((a, b) => millis(b.at) - millis(a.at));
      this.unseenPeople = others
        .filter((p) => readBy[p.id] !== true)
        .sort((a, b) => a.name.localeCompare(b.name, "mn"));
      this.seenOpen = true;
    },
    seenBy(message) {
      const readBy = message.readBy || {};
      return Object.keys(readBy).some(
        (id) => id !== message.senderId && readBy[id] === true,
      );
    },
    roomKindLabel(room) {
      if (room.userType === "student") return "Сурагч";
      if (room.userType === "parent") return "Эцэг эх";
      return "Багш, ажилтан";
    },
    /** "(3) Багшийн систем" — the browser tab is the whole notification. */
    showUnreadInTitle() {
      const count = this.totalUnread;
      document.title = count ? `(${count}) ${this.baseTitle}` : this.baseTitle;
    },

    openRoom(room) {
      // A room object with no id (a creation that half-failed, a stale row)
      // used to leave the panel on its spinner for ever: watchMessages threw
      // before it could ever call back.
      if (!room || !room.id) {
        this.loadingMessages = false;
        console.warn("openRoom: room has no id", room);
        this.$swal.fire("Алдаа", "Чат нээгдсэнгүй. Дахин оролдоно уу.", "warning");
        return;
      }

      this.stuck = true;
      this.chatLock = this.lockFor(room);
      const index = this.tabs.findIndex((t) => t.value === roomScope(room));
      if (index !== -1) this.tab = index;

      this.activeRoom = room;
      this.roomPeople = null;
      this.mentioned = [];
      this.mentionAt = null;
      this.minimized = false;
      this.listOpen = false;
      this.messages = [];
      this.loadingMessages = true;
      if (this.stopMessages) this.stopMessages();

      if (isNationalId(room.id)) this.loadNationalNames(room);
      touchMyMember(this.school, room, this.userData);
      this.checkPending(room);
      syncRoomParticipants(this.school, room)
        .then((synced) => {
          if (!synced) return;
          if (!this.activeRoom || this.activeRoom.id !== room.id) return;
          this.activeRoom = { ...this.activeRoom, participants: synced.participants };
        })
        .catch((e) => console.warn("гишүүд тааруулж чадсангүй", e));

      this.stopMessages = watchMessages(
        this.school,
        room.id,
        this.me.id,
        (messages) => {
          this.messages = messages;
          this.loadingMessages = false;
          if (this.stuck) this.$nextTick(this.scrollToBottom);
          if (!this.minimized) {
            markRead(this.school, room, messages, this.me.id);
          }
        },
      );
    },
    closeRoom() {
      if (this.stopMessages) this.stopMessages();
      this.stopMessages = null;
      this.activeRoom = null;
      this.messages = [];
    },
    scrollToBottom() {
      const box = this.$refs.scroller;
      if (box) box.scrollTop = box.scrollHeight;
    },
    // Reacting to or editing an old message must not drag the view down.
    onScroll(event) {
      const box = event.currentTarget;
      this.stuck = box.scrollHeight - box.scrollTop - box.clientHeight < 80;
    },
    async saveImage(url) {
      this.downloading = true;
      try {
        await downloadFile(url);
      } catch (e) {
        this.$swal.fire("", "Зургийг татаж чадсангүй", "warning");
      } finally {
        this.downloading = false;
      }
    },
    viewImage(src) {
      this.viewerSrc = src;
      this.imageViewer = true;
    },
    pickImage(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      this.sendImageFile(file);
    },
    dropImage(event) {
      const file = event.dataTransfer && event.dataTransfer.files[0];
      if (file && file.type.startsWith("image/")) this.sendImageFile(file);
    },
    async sendImageFile(file) {
      if (!file || !this.activeRoom || this.sendingImage) return;

      this.sendingImage = true;
      try {
        await sendImage(this.school, this.activeRoom, this.me, file);
      } catch (e) {
        console.log("sendImage", e);
        this.$swal.fire("", "Зураг илгээгдсэнгүй", "warning");
      } finally {
        this.sendingImage = false;
      }
    },
    // Facebook-style: "@" plus what follows it filters the room's people above the box.
    onDraftInput(value) {
      const text = String(value || "");
      const input = this.$refs.draftInput && this.$refs.draftInput.$refs.input;
      const caret = input ? input.selectionStart : text.length;
      let at = null;
      if (this.activeRoom && this.activeRoom.type === "group" && caret > 0) {
        const i = text.lastIndexOf("@", caret - 1);
        const picked = this.mentioned.some((p) => text.startsWith("@" + p.name, i) && caret >= i + p.name.length + 1);
        const query = i >= 0 ? text.slice(i + 1, caret) : "";
        if (i >= 0 && !picked && (i === 0 || !text[i - 1].trim()) && !query.includes("\n") && query.length <= 25) at = i;
      }
      this.mentionAt = at;
      this.mentionQuery = at === null ? "" : text.slice(at + 1, caret);
      this.mentionIndex = 0;
      if (at !== null) this.loadRoomPeople();
    },
    async loadRoomPeople() {
      if (this.roomPeople || this.loadingMentions) return;
      this.loadingMentions = true;
      const room = this.activeRoom;
      const key = (p) => String(p.name || "").split(".").pop().trim().toLowerCase();
      try {
        const people = (await loadMembers(this.school, this.year, room)).filter((p) => p.id !== this.me.id);
        if (this.activeRoom === room) this.roomPeople = people.sort((a, b) => key(a).localeCompare(key(b), "mn"));
      } finally {
        this.loadingMentions = false;
      }
    },
    pickMention(person) {
      const at = this.mentionAt;
      if (at === null) return;
      const input = this.$refs.draftInput && this.$refs.draftInput.$refs.input;
      const caret = Math.max(at + 1, input ? input.selectionStart : this.draft.length);
      const insert = "@" + person.name + " ";
      this.draft = this.draft.slice(0, at) + insert + this.draft.slice(caret);
      if (!this.mentioned.some((p) => p.id === person.id)) this.mentioned.push(person);
      this.mentionAt = null;
      this.mentionQuery = "";
      this.$nextTick(() => {
        if (!input) return;
        input.focus();
        input.setSelectionRange(at + insert.length, at + insert.length);
      });
    },
    // Arrows walk the list, Esc closes it.
    onDraftKeydown(e) {
      if (this.mentionAt === null) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        this.mentionAt = null;
        return;
      }
      const n = this.mentionMatches.length;
      if (!n || (e.key !== "ArrowDown" && e.key !== "ArrowUp")) return;
      e.preventDefault();
      this.mentionIndex = (this.mentionIndex + (e.key === "ArrowDown" ? 1 : -1) + n) % n;
      this.$nextTick(() => {
        const row = this.$el.querySelector(".mention-row.is-active");
        if (row) row.scrollIntoView({ block: "nearest" });
      });
    },
    onDraftEnter() {
      if (this.mentionMatches.length) this.pickMention(this.mentionMatches[this.mentionIndex] || this.mentionMatches[0]);
      else this.send();
    },
    async send() {
      if (this.editTarget) return this.saveEdit();
      const text = this.draft;
      if (!this.activeRoom || !text.trim() || this.sending) return;

      const replyToId = this.replyTarget ? this.replyTarget.id : null;
      const mentions = [];
      for (const p of this.mentioned) {
        if (text.includes("@" + p.name) && !mentions.some((m) => m.id === p.id)) {
          mentions.push({ id: p.id, name: p.name });
        }
      }
      this.sending = true;
      this.stuck = true;
      this.draft = "";
      this.mentionAt = null;
      this.replyTarget = null;
      try {
        await sendMessage(this.school, this.activeRoom, this.me, text, replyToId, mentions);
        this.mentioned = [];
      } catch (e) {
        this.draft = text;
        this.$swal.fire("", "Мессеж илгээгдсэнгүй", "warning");
      } finally {
        this.sending = false;
      }
    },
    canDeleteRoom: canDeleteRoom,
    /** Groups you made yourself: hidden for everyone, messages left alone. */
    async removeRoom(room) {
      const result = await this.$swal.fire({
        title: room.name + " чатыг устгах уу?",
        text: "Бүх гишүүдийн жагсаалтаас алга болно.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Устга",
        cancelButtonText: "Болих",
      });
      if (!result.isConfirmed) return;

      const error = await deleteRoom(this.school, room, this.me.id);
      if (error) {
        this.$swal.fire("", error, "warning");
        return;
      }
      if (this.activeRoom && this.activeRoom.id === room.id) this.closeRoom();
    },
    async openPoll() {
      this.pollQuestion = "";
      this.pollOptions = [{ text: "" }, { text: "" }];
      this.pollAnonymous = true;
      this.pollMultiple = false;
      if (!this.pollPresets.length) {
        this.pollPresets = await pollColorPresets();
      }
      this.pollColorsPick = this.pollPresets[0] || null;
      this.pollOpen = true;
    },
    async askPoll() {
      this.pollSending = true;
      try {
        await sendPoll(this.school, this.activeRoom, this.me, {
          question: this.pollQuestion,
          options: this.pollOptions.map((option) => option.text),
          anonymous: this.pollAnonymous,
          multiple: this.pollMultiple,
          colors: this.pollColorsPick,
        });
        this.pollOpen = false;
      } catch (e) {
        this.$swal.fire("", "Санал асуулга илгээгдсэнгүй", "warning");
      } finally {
        this.pollSending = false;
      }
    },
    canEdit(message) {
      if (!this.isMine(message) || message.isDeleted) return false;
      return !message.type || message.type === "text";
    },
    startEdit(message) {
      this.replyTarget = null;
      this.editTarget = message;
      this.draft = message.content || "";
      this.focusDraft();
    },
    startReply(message) {
      this.editTarget = null;
      this.replyTarget = message;
      this.focusDraft();
    },
    replyOf(message) {
      if (!message.replyToId) return null;
      return this.messages.find((m) => m.id === message.replyToId) || null;
    },
    replySnippet(message) {
      if (message.isDeleted) return "Устгасан мессеж";
      if (message.type === "image") return "Зураг";
      if (message.type === "file") return message.fileName || "Файл";
      if (message.type === "poll") return "Санал асуулга";
      const text = String(message.content || "");
      return text.length > 60 ? text.slice(0, 60) + "…" : text;
    },
    focusDraft() {
      this.$nextTick(() => {
        const input = this.$refs.draftInput;
        if (input && input.focus) input.focus();
      });
    },
    cancelEdit() {
      if (this.editTarget) this.draft = "";
      this.editTarget = null;
      this.replyTarget = null;
    },
    async saveEdit() {
      const text = String(this.draft || "").trim();
      const message = this.editTarget;
      if (!message || !text || text === message.content) {
        this.cancelEdit();
        return;
      }
      this.editSaving = true;
      try {
        await editMessage(this.school, this.activeRoom.id, message.id, text);
        this.cancelEdit();
      } catch (e) {
        this.$swal.fire("", "Мессежийг засаж чадсангүй", "warning");
      } finally {
        this.editSaving = false;
      }
    },
    async removeMessage(message) {
      const result = await this.$swal.fire({
        title: "Мессежийг устгах уу?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Устга",
        cancelButtonText: "Болих",
      });
      if (!result.isConfirmed) return;
      await deleteMessage(this.school, this.activeRoom.id, message.id);
    },

    canAddMembers: canAddMembers,
    closeNewChat() {
      this.newChatOpen = false;
      this.addMode = false;
      this.selected = [];
    },
    async openAddMembers() {
      await this.openNewChat({ adding: true });
    },
    async openNewChat({ adding = false } = {}) {
      this.addMode = adding;
      this.newChatOpen = true;
      this.selected = [];
      this.groupName = "";

      if (!this.classes.length) {
        this.classes = await loadClasses(this.school, this.year);
        if (this.classes.length) this.pickClassId = this.classes[0].id;
      }
      await this.loadPeople();
    },
    async loadPeople() {
      this.loadingPeople = true;
      try {
        if (this.pickKind === "teacher") {
          this.people = await loadStaff(this.school, this.me.id);
        } else if (this.pickClassId) {
          this.people = await loadClassMembers(
            this.school,
            this.year,
            this.pickClassId,
            this.pickKind === "parent",
          );
        } else {
          this.people = [];
        }
      } finally {
        this.loadingPeople = false;
      }
    },
    isSelected(person) {
      return this.selected.some((p) => p.id === person.id);
    },
    toggle(person) {
      if (this.isSelected(person)) {
        this.selected = this.selected.filter((p) => p.id !== person.id);
      } else {
        this.selected = [...this.selected, person];
      }
    },
    async confirmAddMembers() {
      this.starting = true;
      try {
        const error = await addMembersToGroup(
          this.school,
          this.activeRoom,
          this.me,
          this.selected,
        );
        if (error) {
          this.$swal.fire("", error, "warning");
          return;
        }
        this.newChatOpen = false;
        this.addMode = false;
        this.selected = [];
        if (this.membersOpen) {
          this.members = await loadMembers(this.school, this.year, this.activeRoom);
        }
      } catch (e) {
        console.error(e);
        this.$swal.fire("Алдаа", "Гишүүн нэмж чадсангүй", "error");
      } finally {
        this.starting = false;
      }
    },
    async startChat() {
      if (!this.selected.length) return;
      if (this.addMode) return this.confirmAddMembers();
      if (this.selected.length > 1 && !this.groupName.trim()) {
        this.$swal.fire("", "Группын нэрээ бичнэ үү", "warning");
        return;
      }

      this.starting = true;
      try {
        const room =
          this.selected.length === 1
            ? await openDirectChat(this.school, this.me, this.selected[0])
            : await createGroupChat(
                this.school,
                this.me,
                this.selected,
                this.groupName.trim(),
              );

        this.newChatOpen = false;
        this.openRoom(room);
      } catch (e) {
        console.error(e);
        this.$swal.fire("Алдаа", "Чат үүсгэж чадсангүй", "error");
      } finally {
        this.starting = false;
      }
    },
  },
  watch: {
    pickKind() {
      this.selected = [];
      this.loadPeople();
    },
    totalUnread() {
      chatBus.$emit("unread", this.totalUnread);
      this.showUnreadInTitle();
    },
    minimized(folded) {
      if (folded || !this.activeRoom || !this.messages.length) return;
      markRead(this.school, this.activeRoom, this.messages, this.me.id);
      this.$nextTick(this.scrollToBottom);
    },
  },
};
</script>

<style>
/* The dashboard theme caps every drawer at 15.625rem, which is why the chat
   list stayed narrow no matter what width was asked for. */
.chat-drawer {
  max-width: none !important;
}
.seen-tick {
  cursor: pointer;
}

.chat-image {
  cursor: pointer;
}
</style>

<style scoped>
/* On a phone the conversation takes the whole screen instead of sitting in
   a corner box that would not fit. */
@media (max-width: 600px) {
  .chat-window {
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
  .chat-window--small {
    height: auto;
  }
}
.chat-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}
.chat-tabs >>> .v-tab {
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
  min-width: 0;
}

.chat-window {
  position: fixed;
  right: 24px;
  bottom: 0;
  width: 360px;
  height: 460px;
  display: flex;
  flex-direction: column;
  border-radius: 10px 10px 0 0;
  overflow: hidden;
  z-index: 12;
}
.chat-window--small {
  height: auto;
}
.chat-window__bar {
  background-color: #ffffff;
}
.chat-window__locked {
  color: #64748b;
  font-size: 14px;
}
.chat-window__composer {
  background-color: #ffffff;
  border-top: 1px solid #eceff1;
}
.chat-window__composer {
  position: relative;
}
.mention-panel {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(100% + 4px);
  z-index: 10;
  max-height: 220px;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}
.mention-row {
  cursor: pointer;
  font-size: 15px;
}
.mention-row:hover,
.mention-row.is-active {
  background: #e8f0fe;
}
.viewer-backdrop {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.92);
}
.viewer-actions {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
}
.viewer-action {
  background: rgba(255, 255, 255, 0.15);
}
.viewer-img {
  max-width: 92vw;
  max-height: 88vh;
  object-fit: contain;
}
.mention-num {
  width: 28px;
  flex-shrink: 0;
  color: #64748b;
  font-size: 13px;
}
.chat-mention {
  color: #2563eb;
  font-weight: 600;
}
.chat-mention-me {
  background: rgba(37, 99, 235, 0.15);
  border-radius: 4px;
  padding: 0 2px;
}
.bubble-mine .chat-mention {
  color: inherit;
}
.bubble-mine .chat-mention-me {
  background: rgba(255, 255, 255, 0.25);
}
.chat-window__body {
  flex: 1;
  overflow-y: auto;
  background-color: #ffffff;
}
/* Аппын мессежийн бөмбөлөгтэй ижил өнгө, ижил радиус. */
.bubble-mine {
  background-color: #0084ff;
  color: #ffffff;
  border-radius: 15px;
}
.bubble-other {
  background-color: #f0f0f0;
  color: #1f2937;
  border-radius: 15px;
}

.chat-stamp {
  margin: 6px 0 14px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #8a8d91;
}

.chat-link {
  color: inherit;
  text-decoration: underline;
}

/* Текстээ сонгож хуулах боломжийг хадгална; урт дарахад хөтчийн өөрийн
   контекст цэс гарахыг л хорино. */
.msg-bubble {
  -webkit-touch-callout: none;
}

.reaction-row {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 4px;
  margin-top: -10px;
  padding: 0 10px;
  cursor: pointer;
}

/* Аппынхтай ижил: хоёр талд саарал, гадуураа чатны дэвсгэрийн өнгөт цагираг
   тул бөмбөлгөөс тасарч харагдана. */
.reaction-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 20px;
  border: 2px solid #fff;
  background: #f0f0f0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
}

.reaction-emoji + .reaction-emoji {
  margin-left: 1px;
}

.reaction-total {
  margin-left: 4px;
  font-size: 11px;
}

.reaction-bar {
  display: flex;
  gap: 4px;
  padding: 6px;
  border-radius: 32px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
}

.reaction-pick {
  padding: 6px;
  border-radius: 50%;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.reaction-pick:hover {
  background: #f0f0f0;
}

.reaction-pick.mine {
  background: rgba(0, 132, 255, 0.15);
}

.poll-swatch-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #757575;
  margin-bottom: 8px;
}

.poll-swatches {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}

.poll-swatch {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  border-radius: 50%;
  font-size: 17px;
  cursor: pointer;
}
.option-card {
  border-radius: 20px;
  cursor: pointer;
}
.icon-box {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.msg-row .bubble-wrap {
  position: relative;
  max-width: 100%;
}
.msg-tools {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: none;
  white-space: nowrap;
}
.msg-tools.tools-right {
  left: 100%;
  padding-left: 2px;
}
.msg-tools.tools-left {
  right: 100%;
  padding-right: 2px;
  flex-direction: row-reverse;
}
.msg-row:hover .msg-tools {
  display: flex;
}
.reply-quote {
  border-left: 3px solid currentColor;
  padding: 2px 0 2px 8px;
  margin-bottom: 4px;
  opacity: 0.8;
  font-size: 12px;
}
.reply-author {
  font-weight: 600;
}
.reply-text {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.edit-banner {
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
}
</style>
